/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Dropdown,
  Input,
  Label,
  List,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ValidationError } from "~/generated/graphql";
import { ExternalLink, FormGroup, FormWrap } from "~/components";
import { REGIONS_AWS } from "~/constants/cloudProviderConst";
import { isNotEmpty } from "~/tools";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";
import { ValidationErrors } from "~/views/accounts/AddCapacity/CapacityForm/components";
import {
  FormData,
  AwsFormFields,
  AwsFormSchema,
} from "~/views/cloudAccounts/BillingData/aws/validators";
import { AwsFormCFStackInfo } from "./AwsFormCFStackInfo";
import { AwsFormSupportInfo } from "./AwsFormSupportInfo";

type Props = {
  onSubmit: SubmitHandler<FormData>;
  onPrevStep: () => void;
  validationErrors?: ValidationError[];
};

export function AwsFormManual({
  onSubmit,
  onPrevStep,
  validationErrors,
}: Props) {
  const {
    register,
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormData>({
    resolver: yupResolver(AwsFormSchema),
    mode: "onChange",
  });

  const disableSubmit = !isValid || isNotEmpty(Object.keys(errors));

  return (
    <FormWrap
      footerProps={{
        nextButtonProps: {
          disabled: disableSubmit,
        },
        onPrevClick: onPrevStep,
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <StepContainer counter={1}>
        <AwsFormCFStackInfo uri="https://docs.mca.nordcloudapp.com/docs/visibility/cloud-accounts/aws/add-billing-accounts#create-cost-and-usage-report-cur" />
      </StepContainer>

      <StepContainer counter={2}>
        <CURReportGenerationInfo notes={SETUP_NOTES} />
      </StepContainer>

      <StepContainer counter={3}>
        <Text>Add this information to access your billing data:</Text>
        <ValidationErrors errs={validationErrors} />
        <Spacer height={theme.spacing.spacing04} />

        <FormGroup error={errors[AwsFormFields.ACCOUNT_ID]}>
          <Label
            required
            htmlFor={AwsFormFields.ACCOUNT_ID}
            name="Account ID"
          />
          <Input
            id={AwsFormFields.ACCOUNT_ID}
            {...register(AwsFormFields.ACCOUNT_ID)}
          />
        </FormGroup>

        <FormGroup error={errors[AwsFormFields.BUCKET_ID]}>
          <Label
            required
            htmlFor={AwsFormFields.BUCKET_ID}
            name="Bucket Name"
          />
          <Input
            id={AwsFormFields.BUCKET_ID}
            {...register(AwsFormFields.BUCKET_ID)}
          />
        </FormGroup>

        <FormGroup error={errors[AwsFormFields.REGION]}>
          <Label required name="Region" />
          <Controller
            control={control}
            name={AwsFormFields.REGION}
            rules={{ required: "This field is required." }}
            defaultValue={undefined}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                options={REGIONS_AWS}
                value={value}
                name="Select Region"
                minNumOfOptionsToShowSearchBox={99}
                onChange={onChange}
              />
            )}
          />
        </FormGroup>

        <FormGroup error={errors[AwsFormFields.REPORT_NAME]}>
          <Label
            required
            htmlFor={AwsFormFields.REPORT_NAME}
            name="Report Name"
          />
          <Input
            id={AwsFormFields.REPORT_NAME}
            {...register(AwsFormFields.REPORT_NAME)}
          />
        </FormGroup>

        <FormGroup error={errors[AwsFormFields.REPORT_PATH_PREFIX]}>
          <Label
            required
            htmlFor={AwsFormFields.REPORT_PATH_PREFIX}
            name="Report Path Prefix"
          />
          <Input
            id={AwsFormFields.REPORT_PATH_PREFIX}
            {...register(AwsFormFields.REPORT_PATH_PREFIX)}
          />
        </FormGroup>
      </StepContainer>

      <AwsFormSupportInfo />
    </FormWrap>
  );
}

const SETUP_NOTES = [
  "Use the same bucket name as in step one",
  "In CUR settings, include Resource IDs",
  "Set data refresh settings",
  "Use daily granularity",
  "Not set report versioning",
  'Set the Path Prefix to CUR (no leading "/")',
  "Enable Redshift data integration with .gz compression",
];

function CURReportGenerationInfo(props: { notes: string[] }) {
  const items = props.notes.map((note) => ({ title: note }));

  return (
    <>
      Go to Cost and Usage Report (CUR) and follow the instructions:
      <br />
      <ExternalLink href="https://docs.aws.amazon.com/cur/latest/userguide/cur-create.html">
        https://docs.aws.amazon.com/cur/latest/userguide/cur-create.html
      </ExternalLink>
      <br />
      <br />
      You must do the following while configuring your CUR:
      <List
        unordered
        items={items}
        css={{ marginTop: theme.spacing.spacing04 }}
      />
    </>
  );
}
